import React, { useState, useMemo, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Paper,
  Chip,
  useTheme,
  IconButton,
  CircularProgress,
} from '@material-ui/core'
import {
  Close as CloseIcon,
  FileCopy as FileIcon,
  Psychology as AIIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ContentCopy as CopyIcon,
  Info as InfoIcon,
  Assignment as AssignmentIcon,
} from '@material-ui/icons'
import TimerIcon from '@material-ui/icons/Timer'
import HPOTermChips from './AnnotationView/components/HPOTermChips'
import DiseaseChips from './AnnotationView/components/DiseaseChips'
import { fetchCnvSettings } from '../../api/cnvAnalysis/settings'
import { alpha } from '@material-ui/core/styles'

// Enhanced Expandable Content Component
const ExpandableContentBox = ({
  title,
  content,
  icon,
  theme,
  maxCollapsedHeight = 80,
  showCharacterCount = true,
  showCopyButton = true,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [copied, setCopied] = useState(false)

  const hasContent = content && content.trim().length > 0
  const contentLength = content?.length || 0
  const needsExpansion = contentLength > 300 || (content && content.split('\n').length > 3)

  const handleCopy = async () => {
    if (content) {
      await navigator.clipboard.writeText(content)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleToggle = () => {
    setIsExpanded(!isExpanded)
  }

  return (
    <Box sx={{ mb: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {icon}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
            {title}
          </Typography>
          {showCharacterCount && hasContent && (
            <Chip
              label={`${contentLength} chars`}
              size="small"
              variant="outlined"
              sx={{
                height: 20,
                fontSize: '0.75rem',
                backgroundColor: theme.palette.primary.light,
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                fontWeight: 500,
              }}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {showCopyButton && hasContent && (
            <IconButton
              size="small"
              onClick={handleCopy}
              sx={{
                width: 28,
                height: 28,
                color: copied ? theme.palette.success.main : theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                  color: theme.palette.primary.main,
                },
              }}
            >
              <CopyIcon sx={{ fontSize: '0.9rem' }} />
            </IconButton>
          )}

          {needsExpansion && hasContent && (
            <IconButton
              size="small"
              onClick={handleToggle}
              sx={{
                width: 28,
                height: 28,
                color: theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                  color: theme.palette.primary.main,
                },
              }}
            >
              {isExpanded ? (
                <ExpandLessIcon sx={{ fontSize: '1.1rem' }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: '1.1rem' }} />
              )}
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Content Box */}
      <Box
        sx={{
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '8px',
          overflow: 'hidden',
          transition: 'all 0.3s ease',
          '&:hover': {
            borderColor: theme.palette.primary.main,
            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`,
          },
        }}
      >
        {hasContent ? (
          <>
            <Box
              sx={{
                p: 2,
                overflow: 'hidden',
                overflowX: 'hidden',
                position: 'relative',
                width: '100%',
                maxHeight: isExpanded ? 'none' : maxCollapsedHeight,
                transition: 'max-height 0.3s ease',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  whiteSpace: 'pre-line',
                  lineHeight: 1.6,
                  fontSize: '0.875rem',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}
              >
                {content}
              </Typography>

              {/* Gradient overlay for collapsed state */}
              {!isExpanded && needsExpansion && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 20,
                    background: `linear-gradient(transparent, ${theme.palette.background.paper})`,
                    pointerEvents: 'none',
                  }}
                />
              )}
            </Box>
          </>
        ) : (
          <Box
            sx={{
              p: 2,
              minHeight: 60,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: theme.palette.action.hover,
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <InfoIcon sx={{ fontSize: '1.5rem', color: theme.palette.text.disabled, mb: 1 }} />
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                No {title.toLowerCase()} provided
              </Typography>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                This information helps improve AI analysis accuracy
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  )
}

const AnalysisSummaryPopup = ({ open, onClose, analysisData }) => {
  const theme = useTheme()

  // Custom scrollbar styles
  const scrollbarStyles = {
    '&::-webkit-scrollbar': {
      width: '8px',
      background: theme.palette.background.paper,
      borderRadius: '8px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.primary.main,
      borderRadius: '8px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: theme.palette.primary.dark,
    },
    scrollbarColor: `${theme.palette.primary.main} ${theme.palette.background.paper}`,
    scrollbarWidth: 'thin',
  }

  // Parse analysis configuration - handle both regular and carrier analysis
  const config = useMemo(() => {
    if (!analysisData?.analysis_parameters) return null

    const isCarrierAnalysis = analysisData.analysis_type === 'CARRIER'

    if (isCarrierAnalysis) {
      // Handle carrier analysis structure
      const annotations = analysisData.analysis_parameters.annotation || []

      return {
        isCarrierAnalysis: true,
        sampleName: analysisData.sample_name,
        analysisType: 'Carrier Analysis',
        annotations: annotations,
        carrierConfigId: analysisData.analysis_parameters.carrierConfigId,
        createdAt: analysisData.created_at,
        analysisName: analysisData.analysis_name,
        // For compatibility with existing code
        runAI: false, // Carrier analysis doesn't use AI
        hpos: [],
        hpoNames: [],
        diseases: [],
        clinicalHistory: '',
        userGuidance: '',
        fileType: 'VCF',
        sequencingType: '',
        targetedType: '',
        reference: annotations[0]?.annotation?.reference || '',
        panel: annotations[0]?.annotation?.panelName || annotations[0]?.annotation?.panel || '',
      }
    } else {
      // Handle regular analysis structure
      const annotation = analysisData.analysis_parameters.annotation
      return {
        isCarrierAnalysis: false,
        sampleName: analysisData.sample_name,
        runAI: annotation?.runAI || false,
        hpos: annotation?.hpos || [],
        hpoNames: annotation?.hpoNames || [],
        diseases: annotation?.diseases || [],
        clinicalHistory: annotation?.clinicalHistory || '',
        userGuidance: annotation?.userGuidance || '',
        fileType: analysisData.analysis_type,
        sequencingType: annotation?.type || '',
        targetedType: annotation?.targetedType || '',
        reference: annotation?.reference || '',
        panel: annotation?.panelName || annotation?.panel || '',
        createdAt: analysisData.created_at,
        analysisName: analysisData.analysis_name,
      }
    }
  }, [analysisData])

  // CNV settings fetch when CNV is enabled
  const [cnvSettings, setCnvSettings] = useState(null)
  const [cnvLoading, setCnvLoading] = useState(false)
  const [cnvError, setCnvError] = useState('')

  useEffect(() => {
    const annotation = analysisData?.analysis_parameters?.annotation
    const runCnv = annotation?.runCnv === true || annotation?.runCnv === 'true'
    const analysisWrapperId = analysisData?.id || analysisData?.analysis_id || analysisData?.analysisId

    if (!open || !runCnv || !analysisWrapperId) {
      setCnvSettings(null)
      setCnvError('')
      setCnvLoading(false)
      return
    }

    const fetchCnvData = async () => {
      try {
        setCnvLoading(true)
        setCnvError('')
        const data = await fetchCnvSettings(analysisWrapperId)
        setCnvSettings(data)
      } catch (err) {
        setCnvError(err?.message || 'Failed to load CNV settings')
      } finally {
        setCnvLoading(false)
      }
    }

    fetchCnvData()
  }, [open, analysisData])

  const getConfigurationItems = () => {
    if (!config) return []

    const items = []

    if (config.isCarrierAnalysis) {
      // Carrier analysis specific configuration
      items.push(['Analysis Type', 'Carrier Analysis'])
      items.push(['Number of Samples', analysisData?.sample_name?.split(',').length || 0])
      items.push(['Number of Annotations', config.annotations?.length || 0])

      // Show panel from first annotation if available
      if (config.panel) {
        items.push(['Panel', config.panel])
      }
    } else {
      items.push(['Sample Name', analysisData.sample_name])
      items.push(['File Name', analysisData.file_names.join(', ')])

      // Regular analysis configuration
      const annotation = analysisData?.analysis_parameters?.annotation

      // Analysis type information - smart deduplication
      if (annotation?.analysisType) items.push(['Analysis Type', annotation.analysisType])

      // Sequencing information - show only the most relevant one
      const hasSequencingType = annotation?.type
      // const hasRunType = fileData?.sarek_run_type
      const hasSequencing = config.sequencingType
      const hasTargetedType = annotation?.targetedType
      const runCnv = annotation?.runCnv

      // Priority: Run Type > Sequencing Type > Sequencing
      // if (hasRunType) {
      //   items.push(['Sequencing', fileData.sarek_run_type])
      // } else
      if (hasSequencingType) {
        items.push(['Sequencing', annotation.type])
      } else if (hasSequencing) {
        items.push(['Sequencing', config.sequencingType])
      }

      if (runCnv) {
        if (analysisData.analysis_type === 'ONT_BAM') {
          items.push(['CNV Analysis', 'ONT CNV (Spectre)'])
        } else if (analysisData.analysis_type === 'PACBIO_BAM') {
          items.push(['CNV Analysis', 'PacBio CNV (HiFiCNV)'])
        } else if (cnvLoading) {
          items.push(['CNV Analysis', 'Loading...'])
        } else if (cnvError) {
          items.push(['CNV Analysis', `Error: ${cnvError}`])
        } else if (cnvSettings) {
          const cnvType = cnvSettings.type || 'Unknown'
          let cnvDetails = cnvType

          if (cnvType === 'GENESUS-COHORT' && cnvSettings.cohort) {
            cnvDetails = `${cnvType} - ${cnvSettings.cohort.name}`
          } else if (cnvType === 'ECOLE' && cnvSettings.ecole?.model) {
            cnvDetails = `${cnvType} v0.2`
          } else if (cnvType === 'GATK-MODELSEGMENTS') {
            // The method is no longer offered; its earlier runs read as plain CNV.
            cnvDetails = 'Enabled'
          } else if (cnvType === 'EXOMEDEPTH') {
            cnvDetails = 'EXOMEDEPTH'
          }

          items.push(['CNV Analysis', cnvDetails])
        } else {
          items.push(['CNV Analysis', 'Enabled'])
        }
      }

      const runSv = annotation?.runSv
      if (runSv) {
        if (analysisData.analysis_type === 'PACBIO_BAM') {
          items.push(['SV Analysis', 'PacBio SV (pbsv)'])
        } else {
          items.push(['SV Analysis', 'ONT SV (Sniffles2)'])
        }
      }

      // // Only show Targeted Method if it's different from Sequencing
      // if (hasTargetedType && hasSequencingType && annotation.type === 'Targeted') {
      //   const sequencingValue = hasRunType ? fileData.sarek_run_type : annotation.type
      //   if (sequencingValue !== annotation.targetedType) {
      //     items.push(['Targeted Method', annotation.targetedType])
      //   }
      // }

      // Analysis configuration
      if (config.fileType) items.push(['File Type', config.fileType])
      if (config.reference) items.push(['Reference', config.reference])
      if (config.panel) items.push(['Capture Kit', config.panel])

      if (cnvSettings?.cohort && (cnvSettings.type === 'GENESUS-CNV' || cnvSettings.type === 'EXOMEDEPTH')) {
        items.push(['Cohort', cnvSettings.cohort.name])
      }
    }

    return items
  }

  // Helper function to get common variables
  const getAnalysisVariables = () => {
    const annotation = analysisData?.analysis_parameters?.annotation
    const fileType = analysisData?.analysis_type || 'Unknown'
    const isVCF = fileType === 'VCF'
    const isCarrier = fileType === 'CARRIER'
    return { annotation, fileType, isVCF, isCarrier }
  }

  const getChipDetails = (label, value) => {
    const { annotation, fileType, isVCF, isCarrier } = getAnalysisVariables()

    // Carrier Analysis chips
    if (isCarrier && label === 'Analysis Type') {
      return { label: 'Carrier Analysis', color: 'secondary' }
    }

    // Analysis Type chips - match table logic exactly
    if (label === 'Analysis Type') {
      if (isVCF) {
        return { label: 'VCF', color: 'default' }
      }

      if (annotation?.analysisType) {
        switch (annotation.analysisType) {
          case 'Germline':
            return { label: 'Germline', color: 'primary' }
          case 'TumorOnly':
            return { label: 'Tumor-Only', color: 'secondary' }
          case 'TumorNormal':
            return { label: 'Tumor/Normal', color: 'secondary' }
          case 'TumorOnlyRNA':
            return { label: 'Tumour RNA', color: 'secondary' }
          default:
            return { label: annotation.analysisType, color: 'default' }
        }
      }

      return { label: fileType, color: 'default' }
    }

    // Carrier Config ID chips
    if (label === 'Carrier Config ID') {
      return { label: value, color: 'info', variant: 'outlined' }
    }

    // Number of Samples/Annotations chips
    if (label === 'Number of Samples' || label === 'Number of Annotations') {
      return { label: value.toString(), color: 'primary', variant: 'outlined' }
    }

    // Sequencing chips - match table logic exactly
    if (label === 'Sequencing') {
      // Handle run type values (from sarek_run_type)
      if (value.includes('WholeGenome')) {
        return { label: 'WGS', color: 'success' }
      } else if (value.includes('Exome')) {
        return { label: 'WES', color: 'info' }
      } else if (value.includes('TumorNormal')) {
        return { label: 'Tumor/Normal', color: 'secondary' }
      } else if (value.includes('TumorOnly')) {
        return { label: 'Tumor-Only', color: 'secondary' }
      } else if (value.includes('Germline')) {
        return { label: 'Germline', color: 'primary' }
      }

      // Handle annotation.type values
      if (annotation?.type === 'WholeGenome') {
        return { label: 'WGS', color: 'success' }
      } else if (annotation?.type === 'Targeted' && annotation?.targetedType) {
        const labelText =
          annotation.targetedType === 'Clinical Exome'
            ? 'Clinical Exome'
            : annotation.targetedType === 'Whole Exome'
            ? 'WES'
            : annotation.targetedType
        return { label: labelText, color: 'info' }
      }

      return { label: value, color: 'default' }
    }

    // Targeted Method chips - match table logic exactly
    if (label === 'Targeted Method') {
      if (annotation?.targetedType) {
        const labelText =
          annotation.targetedType === 'Clinical Exome'
            ? 'Clinical Exome'
            : annotation.targetedType === 'Whole Exome'
            ? 'WES'
            : annotation.targetedType
        return { label: labelText, color: 'info' }
      }
      return { label: value, color: 'info' }
    }

    // CNV Analysis chips
    if (label === 'CNV Analysis') {
      if (value.includes('Loading')) {
        return { label: 'Loading...', color: 'default', variant: 'outlined' }
      }
      if (value.includes('Error')) {
        return { label: 'Error', color: 'error', variant: 'outlined' }
      }
      if (value.includes('GENESUS-CNV')) {
        return { label: value, color: 'primary', variant: 'filled' }
      }
      if (value.includes('ECOLE')) {
        return { label: value, color: 'info', variant: 'filled' }
      }
      if (value.includes('GENESUS-COHORT')) {
        return { label: value, color: 'info', variant: 'filled' }
      }
      return { label: value, color: 'default', variant: 'outlined' }
    }

    // SV Analysis chips
    if (label === 'SV Analysis') {
      return { label: value, color: 'info', variant: 'filled' }
    }

    // File Type chips
    if (label === 'File Type') {
      if (isVCF) {
        return { label: 'VCF', color: 'default' }
      }
      switch (value) {
        case 'VCF':
          return { label: 'VCF', color: 'default' }
        case 'FASTQ':
          return { label: 'FASTQ', color: 'success' }
        case 'ONT_BAM':
          return { label: 'ONT BAM', color: 'secondary' }
        case 'PACBIO_BAM':
          return { label: 'PacBio BAM', color: 'default' }
        default:
          return { label: value, color: 'default' }
      }
    }

    // Reference chips
    if (label === 'Reference') {
      return { label: value, color: 'primary', variant: 'outlined' }
    }

    // Capture Kit chips
    if (label === 'Capture Kit' || label === 'Panel') {
      return { label: value, color: 'info', variant: 'outlined' }
    }

    // Default - return null to use text instead of chip
    return null
  }

  const getAIConfigurationItems = () => {
    if (!config || !config.runAI) return []

    const items = []

    items.push(['HPO Terms', config.hpos.length ? `${config.hpos.length} selected` : 'None'])
    items.push(['Diseases', config.diseases.length ? `${config.diseases.length} selected` : 'None'])
    items.push(['Clinical History', config.clinicalHistory ? 'Provided' : 'Not provided'])
    items.push(['Analysis Guidance', config.userGuidance ? 'Provided' : 'Not provided'])

    return items
  }

  // Get carrier analysis specific items
  const getCarrierAnalysisItems = () => {
    if (!config?.isCarrierAnalysis) return []

    const items = []

    // Add sample information
    items.push(['Sample Names', analysisData.sample_name])
    items.push(['File Names', analysisData.file_names.join(', ')])

    // Add annotation information
    if (config.annotations && config.annotations.length > 0) {
      const roles = config.annotations.map((ann) => ann.role).filter(Boolean)
      if (roles.length > 0) {
        items.push(['Sample Roles', roles.join(', ')])
      }
    }

    return items
  }

  if (!config) return null

  // Add getHPOTermDetails and getDiseaseDetails (copy from StepReview)
  const getHPOTermDetails = (term, termIndex = 0, hpos) => {
    if (hpos?.hpoTerms && hpos.hpoTerms[termIndex]) {
      const fullTerm = hpos.hpoTerms[termIndex]
      const synonyms = []
      if (fullTerm?.synonyms && Array.isArray(fullTerm.synonyms)) synonyms.push(...fullTerm.synonyms)
      if (fullTerm?.synonym && Array.isArray(fullTerm.synonym)) synonyms.push(...fullTerm.synonym)
      if (fullTerm?.alt_ids && Array.isArray(fullTerm.alt_ids)) synonyms.push(...fullTerm.alt_ids)
      if (fullTerm?.alternative_ids && Array.isArray(fullTerm.alternative_ids))
        synonyms.push(...fullTerm.alternative_ids)
      return {
        id: fullTerm?.hpo_id || fullTerm?.id || fullTerm?.value || fullTerm?.hpo || 'Unknown',
        name: fullTerm?.name || fullTerm?.label || fullTerm?.term || fullTerm?.text || 'Unknown phenotype',
        description: fullTerm?.description || fullTerm?.def || fullTerm?.definition || fullTerm?.comment || '',
        synonyms: [...new Set(synonyms)],
        definition: fullTerm?.definition || fullTerm?.def || null,
        comment: fullTerm?.comment || null,
        category: fullTerm?.category || fullTerm?.namespace || fullTerm?.class || null,
        xrefs: fullTerm?.xrefs || [],
        is_obsolete: fullTerm?.is_obsolete || false,
        replaced_by: fullTerm?.replaced_by || null,
        creation_date: fullTerm?.creation_date || null,
      }
    }
    if (term && typeof term === 'object') {
      const synonyms = []
      if (term?.synonyms && Array.isArray(term.synonyms)) synonyms.push(...term.synonyms)
      if (term?.synonym && Array.isArray(term.synonym)) synonyms.push(...term.synonym)
      if (term?.alt_ids && Array.isArray(term.alt_ids)) synonyms.push(...term.alt_ids)
      if (term?.alternative_ids && Array.isArray(term.alternative_ids)) synonyms.push(...term.alternative_ids)
      return {
        id: term?.hpo_id || term?.id || term?.value || term?.hpo || 'Unknown',
        name: term?.name || term?.label || term?.term || term?.text || 'Unknown phenotype',
        description:
          term?.description ||
          term?.def ||
          term?.definition ||
          term?.comment ||
          (term?.name
            ? `${term.name} - A phenotypic abnormality described in the Human Phenotype Ontology`
            : 'Human Phenotype Ontology term describing a phenotypic abnormality'),
        synonyms: [...new Set(synonyms)],
        definition: term?.definition || term?.def || term?.description || null,
        comment: term?.comment || null,
        category: term?.category || term?.namespace || term?.class || null,
        xrefs: term?.xrefs || [],
        is_obsolete: term?.is_obsolete || false,
        replaced_by: term?.replaced_by || null,
        creation_date: term?.creation_date || null,
      }
    }
    if (typeof term === 'string') {
      const hpoName = hpos?.hpoNames?.[termIndex] || term
      return {
        id: term,
        name: hpoName !== term ? hpoName : `HPO:${term}`,
        description: 'Human Phenotype Ontology term describing a phenotypic abnormality',
        synonyms: [],
        definition: null,
        category: null,
        comment: null,
      }
    }
    return {
      id: 'Unknown',
      name: 'Unknown phenotype',
      description: 'Human Phenotype Ontology term',
      synonyms: [],
      definition: null,
      category: null,
      comment: null,
    }
  }

  const getDiseaseDetails = (disease) => {
    if (typeof disease === 'string') {
      return {
        name: disease,
        description: 'Genetic disease or condition',
        omim: null,
        inheritance: null,
        synonyms: [],
        genes: [],
        phenotypes: [],
      }
    }
    const synonyms = []
    if (disease?.synonyms && Array.isArray(disease.synonyms)) synonyms.push(...disease.synonyms)
    if (disease?.alt_names && Array.isArray(disease.alt_names)) synonyms.push(...disease.alt_names)
    const genes = []
    if (disease?.genes && Array.isArray(disease.genes)) genes.push(...disease.genes)
    if (disease?.associated_genes && Array.isArray(disease.associated_genes)) genes.push(...disease.associated_genes)
    return {
      name: disease?.name || disease?.label || disease?.disease || disease?.title || 'Unknown disease',
      description: disease?.description || disease?.definition || 'Genetic disease or medical condition',
      omim: disease?.omim || disease?.omim_id || null,
      inheritance: disease?.inheritance || disease?.inheritance_pattern || null,
      synonyms: [...new Set(synonyms)],
      genes: [...new Set(genes)],
      phenotypes: disease?.phenotypes || disease?.hpo_terms || [],
      category: disease?.category || disease?.disease_class || null,
      prevalence: disease?.prevalence || null,
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          maxHeight: '90vh',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          background: theme.palette.background.paper,
          ...scrollbarStyles,
        },
      }}
    >
      {/* Enhanced Header */}
      <DialogTitle
        sx={{
          background: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          pb: 3,
          pt: 3,
          px: 4,
          borderRadius: '16px 16px 0 0',
          marginBottom: '10px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                backgroundColor: alpha(theme.palette.primary.contrastText, 0.16),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {config.runAI ? (
                <AIIcon sx={{ fontSize: '1.5rem', color: 'inherit' }} />
              ) : (
                <AssignmentIcon sx={{ fontSize: '1.5rem', color: 'inherit' }} />
              )}
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, letterSpacing: '0.5px' }}>
                {config.isCarrierAnalysis ? 'Carrier Analysis Configuration' : 'Analysis Configuration'}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {config.analysisName || 'Unknown Sample'}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            size="large"
            sx={{
              color: 'inherit',
              backgroundColor: alpha(theme.palette.primary.contrastText, 0.12),
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.contrastText, 0.24),
                transform: 'scale(1.1)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 4, py: 4, ...scrollbarStyles }}>
        <Grid container spacing={3}>
          {/* File Configuration */}
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: '12px',
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                height: '100%',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <FileIcon sx={{ color: theme.palette.info.main, fontSize: '1.5rem' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                  {config.isCarrierAnalysis ? 'Carrier Analysis Configuration' : 'File Configuration'}
                </Typography>
              </Box>

              {getConfigurationItems().map(([label, value], index) => {
                const chipDetails = getChipDetails(label, value)
                return (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 1.5,
                      borderBottom:
                        index < getConfigurationItems().length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                    }}
                  >
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                      {label}
                    </Typography>
                    {chipDetails ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Chip
                          size="small"
                          label={chipDetails.label}
                          color={chipDetails.color}
                          variant={chipDetails.variant || 'filled'}
                          sx={{
                            fontSize: '0.75rem',
                            height: 24,
                            fontWeight: 600,
                            '& .MuiChip-label': {
                              px: 1,
                            },
                          }}
                        />
                        {label === 'Analysis Type' &&
                          (() => {
                            const { isVCF, annotation, isCarrier } = getAnalysisVariables()
                            return !isVCF && !isCarrier && annotation?.runAI
                          })() && <AIIcon sx={{ fontSize: '1rem', color: 'secondary.main' }} />}
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                        {value}
                      </Typography>
                    )}
                  </Box>
                )
              })}
            </Paper>
          </Grid>

          {/* Carrier Analysis Details */}
          {config.isCarrierAnalysis && (
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: '12px',
                  backgroundColor: `${alpha(theme.palette.secondary.main, 0.03)}`,
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.19)}`,
                  height: '100%',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.secondary.main,
                    }}
                  >
                    Sample Information
                  </Typography>
                </Box>

                {getCarrierAnalysisItems().map(([label, value], index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      py: 1.5,
                      borderBottom:
                        index < getCarrierAnalysisItems().length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                    }}
                  >
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                      {label}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        maxWidth: '60%',
                        textAlign: 'right',
                        wordBreak: 'break-word',
                      }}
                    >
                      {value}
                    </Typography>
                  </Box>
                ))}

                {/* Sample Details Table */}

                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}>
                    Individual Sample Details
                  </Typography>
                  <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                    {analysisData?.sample_name?.split(', ').map((sample, index) => {
                      const annotation = config.annotations[index]
                      const status = analysisData.status

                      return (
                        <Box
                          key={sample || index}
                          sx={{
                            p: 2,
                            mb: 1,
                            borderRadius: '8px',
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {sample || `Sample ${index + 1}`}
                            </Typography>
                            <Chip
                              size="small"
                              label={status}
                              color={status === 'DONE' ? 'success' : status === 'RUNNING' ? 'warning' : 'default'}
                              sx={{ fontSize: '0.75rem', height: 20 }}
                            />
                          </Box>
                          {/* <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                            {sample || 'Unknown file'}
                          </Typography> */}
                          {annotation && (
                            <Box sx={{ mt: 1 }}>
                              <Chip
                                size="small"
                                label={annotation.role || 'Unknown Role'}
                                color="info"
                                variant="outlined"
                                sx={{ fontSize: '0.75rem', height: 18 }}
                              />
                            </Box>
                          )}
                        </Box>
                      )
                    })}
                  </Box>
                </Box>
              </Paper>
            </Grid>
          )}

          {/* AI Configuration - only for regular analysis */}
          {!config.isCarrierAnalysis && config.runAI && (
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: '12px',
                  backgroundColor: config.runAI
                    ? `${alpha(theme.palette.secondary.main, 0.03)}`
                    : theme.palette.background.paper,
                  border: `1px solid ${
                    config.runAI ? alpha(theme.palette.secondary.main, 0.19) : theme.palette.divider
                  }`,
                  height: '100%',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: config.runAI ? theme.palette.secondary.main : theme.palette.text.primary,
                    }}
                  >
                    {config.runAI ? 'AI-Powered Analysis' : 'Standard Analysis'}
                  </Typography>
                </Box>
                {config.runAI && (
                  <>
                    {/* HPO Terms */}
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: theme.palette.text.primary }}>
                      HPO Terms
                    </Typography>
                    <Box sx={{ width: '100%', mb: 2 }}>
                      <HPOTermChips
                        hpos={config}
                        getHPOTermDetails={(term, idx) => getHPOTermDetails(term, idx, config)}
                      />
                    </Box>
                    {/* Diseases */}
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, mb: 1, color: theme.palette.text.primary, mt: 2 }}
                    >
                      Diseases
                    </Typography>
                    <Box sx={{ width: '100%', mb: 2 }}>
                      <DiseaseChips diseases={config.diseases} getDiseaseDetails={getDiseaseDetails} />
                    </Box>
                    {/* Clinical History */}
                    <ExpandableContentBox
                      title="Clinical History"
                      content={config.clinicalHistory}
                      theme={theme}
                      maxCollapsedHeight={80}
                      showCharacterCount={true}
                      showCopyButton={true}
                    />

                    {/* Analysis Guidance */}
                    <ExpandableContentBox
                      title="Analysis Guidance"
                      content={config.userGuidance}
                      theme={theme}
                      maxCollapsedHeight={80}
                      showCharacterCount={true}
                      showCopyButton={true}
                    />
                  </>
                )}
              </Paper>
            </Grid>
          )}

          {/* Metadata */}
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: '8px',
                backgroundColor: theme.palette.dark,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TimerIcon sx={{ fontSize: '1rem', color: theme.palette.text.secondary }} />
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                  Created:{' '}
                  {new Date(config.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 4, pb: 4, pt: 0 }}>
        <Button
          onClick={onClose}
          variant="contained"
          color="primary"
          sx={{
            textTransform: 'none',
            borderRadius: '8px',
            px: 4,
            py: 1.5,
            fontWeight: 600,
            fontSize: '0.9rem',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            '&:hover': {
              boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
              transform: 'translateY(-1px)',
            },
            transition: 'all 0.2s ease',
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AnalysisSummaryPopup
